import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Image,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuTrigger,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react';

import { BellRinging, CheckCircle, DotsThree, EnvelopeOpen, Funnel } from '@phosphor-icons/react';

import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';

import {
  apiDeleteInbox,
  apiGetInbox,
  apiGetUnreadCount,
  apiMarkAllInboxAsRead,
  apiMarkInboxAsRead,
  GetInboxResponse
} from '@/api/services/inbox.service';

import { fetchAPI } from '@/api/fetch';
import { toaster } from '@/components/ui/toaster';

import emptyImage from '@/assets/image/empty-job-cover.svg';
import useAuth from '@/store/auth';

const ITEMS_PER_PAGE = 20;

export default function InboxIndex() {
  const navigate = useNavigate();
  const { account } = useAuth();
  const isAdminSession = account?.login_by_admin || false;
  const [inboxData, setInboxData] = useState<GetInboxResponse | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetchAPI({
        request: () => apiGetUnreadCount()
      });
      if (response.success && response.data) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  const fetchInbox = useCallback(async (page: number = 1, unreadOnly: boolean = false) => {
    try {
      const response = await fetchAPI({
        request: () =>
          apiGetInbox({
            params: {
              page,
              unread: unreadOnly,
              limit: ITEMS_PER_PAGE
            }
          })
      });
      if (response.success && response.data) {
        setInboxData(response.data);
      } else {
        toaster.create({
          title: 'Error',
          description: response.message || 'Failed to fetch inbox messages',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching inbox:', error);
      toaster.create({
        title: 'Error',
        description: 'An error occurred while fetching messages',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInbox(currentPage, showUnreadOnly);
    fetchUnreadCount();
  }, [fetchInbox, fetchUnreadCount, currentPage, showUnreadOnly]);

  const handleMarkAsRead = async (inboxId: string, isRead: boolean) => {
    // Prevent marking as read/unread in admin sessions
    if (isAdminSession) {
      toaster.create({
        title: 'Action Disabled',
        description: 'Cannot modify notifications in admin session',
        type: 'warning'
      });
      return;
    }

    setProcessingIds((prev) => new Set(prev).add(inboxId));
    try {
      const response = await fetchAPI({
        request: () =>
          apiMarkInboxAsRead({
            inboxId,
            payload: { is_read: !isRead }
          })
      });

      if (response.success) {
        // Update local state
        const newUnreadCount = !isRead ? unreadCount - 1 : unreadCount + 1;
        setUnreadCount(newUnreadCount);

        // Dispatch custom event to update sidebar
        window.dispatchEvent(
          new CustomEvent('inbox-unread-count-changed', {
            detail: { unreadCount: newUnreadCount }
          })
        );

        setInboxData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) =>
              msg.inbox_id === inboxId ? { ...msg, is_read: !isRead } : msg
            )
          };
        });
      } else {
        toaster.create({
          title: 'Error',
          description: response.message || 'Failed to update message status',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating message status:', error);
      toaster.create({
        title: 'Error',
        description: 'An error occurred while updating message',
        type: 'error'
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(inboxId);
        return next;
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    // Prevent marking all as read in admin sessions
    if (isAdminSession) {
      toaster.create({
        title: 'Action Disabled',
        description: 'Cannot modify notifications in admin session',
        type: 'warning'
      });
      return;
    }

    setIsMarkingAllRead(true);
    try {
      const response = await fetchAPI({
        request: () => apiMarkAllInboxAsRead()
      });

      if (response.success) {
        // Update unread count
        setUnreadCount(0);

        // Dispatch custom event to update sidebar
        window.dispatchEvent(
          new CustomEvent('inbox-unread-count-changed', {
            detail: { unreadCount: 0 }
          })
        );

        setInboxData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((msg) => ({ ...msg, is_read: true }))
          };
        });
        toaster.create({
          title: 'Success',
          description: 'All messages marked as read',
          type: 'success'
        });
      } else {
        toaster.create({
          title: 'Error',
          description: response.message || 'Failed to mark all as read',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      toaster.create({
        title: 'Error',
        description: 'An error occurred while marking all as read',
        type: 'error'
      });
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  const handleDelete = async (inboxId: string) => {
    setProcessingIds((prev) => new Set(prev).add(inboxId));
    try {
      const response = await fetchAPI({
        request: () => apiDeleteInbox({ inboxId })
      });

      if (response.success) {
        setInboxData((prev) => {
          if (!prev) return prev;
          const deletedMsg = prev.messages.find((msg) => msg.inbox_id === inboxId);
          const wasUnread = deletedMsg && !deletedMsg.is_read;

          // Update unread count
          if (wasUnread) {
            const newUnreadCount = unreadCount - 1;
            setUnreadCount(newUnreadCount);

            // Dispatch custom event to update sidebar
            window.dispatchEvent(
              new CustomEvent('inbox-unread-count-changed', {
                detail: { unreadCount: newUnreadCount }
              })
            );
          }

          return {
            ...prev,
            messages: prev.messages.filter((msg) => msg.inbox_id !== inboxId),
            total_count: prev.total_count - 1
          };
        });
        toaster.create({
          title: 'Success',
          description: 'Message deleted',
          type: 'success'
        });
      } else {
        toaster.create({
          title: 'Error',
          description: response.message || 'Failed to delete message',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toaster.create({
        title: 'Error',
        description: 'An error occurred while deleting message',
        type: 'error'
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(inboxId);
        return next;
      });
    }
  };

  const handleCardClick = async (message: Inbox) => {
    if (message.link_path) {
      // Mark as read if unread and wait for completion before navigating
      // Skip marking as read in admin sessions
      if (!message.is_read && !isAdminSession) {
        // Optimistically update sidebar immediately before API call
        const newUnreadCount = Math.max(0, unreadCount - 1);
        window.dispatchEvent(
          new CustomEvent('inbox-unread-count-changed', {
            detail: { unreadCount: newUnreadCount }
          })
        );

        // Then make the API call
        await handleMarkAsRead(message.inbox_id, message.is_read);
      }
      navigate(message.link_path);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
      }
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const toggleExpand = (inboxId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(inboxId)) {
        next.delete(inboxId);
      } else {
        next.add(inboxId);
      }
      return next;
    });
  };

  // Pagination - API handles server-side pagination
  const pageSize = inboxData?.limit || ITEMS_PER_PAGE;
  const totalCount = inboxData?.total_count || 0;
  const currentMessages = inboxData?.messages || [];

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px" w="100%">
        <Spinner size="xl" color="primary.900" />
      </Flex>
    );
  }

  return (
    <Box p={{ base: '20px', lg: '40px' }} w="100%" maxW="1200px" mx="auto">
      {/* Header */}
      <Stack gap="12px" w="100%" mb="24px">
        <Heading fontSize="32px" fontWeight="700" color="neutrals.900" mb="8px">
          Notifications ({unreadCount})
        </Heading>
        <Flex justify="space-between" gap="12px" align="center">
          <Button
            w="fit-content"
            borderRadius="full"
            onClick={() => {
              setShowUnreadOnly(!showUnreadOnly);
              setCurrentPage(1); // Reset to page 1 when toggling filter
            }}
            variant={showUnreadOnly ? 'solid' : 'outline'}
          >
            <Flex gap="8px" align="center">
              <Funnel size={20} weight={showUnreadOnly ? 'fill' : 'regular'} />
              <Text fontSize="14px" color={showUnreadOnly ? 'neutrals.0' : 'neutrals.700'}>
                Show Unread
              </Text>
            </Flex>
          </Button>
          {unreadCount > 0 && (
            <Button
              onClick={handleMarkAllAsRead}
              loading={isMarkingAllRead}
              variant="outline"
              border="1px solid"
              borderColor="neutrals.300"
              color="neutrals.700"
              borderRadius="8px"
            >
              <Flex gap="8px" align="center">
                <EnvelopeOpen size={20} />
                <Text fontSize="14px" color="neutrals.700">
                  Mark All as Read
                </Text>
              </Flex>
            </Button>
          )}
        </Flex>
      </Stack>

      {/* Messages List */}
      {!inboxData || inboxData.messages.length === 0 ? (
        <Flex direction="column" align="center" justify="center" minH="400px" gap="16px">
          <Image src={emptyImage} alt="No notifications" mb="0px" w="240px" />
          <Text fontSize="20px" fontWeight="600" color="neutrals.600">
            No notifications yet
          </Text>
          <Text fontSize="14px" color="neutrals.500">
            You&apos;re all caught up! New notifications will appear here.
          </Text>
        </Flex>
      ) : (
        <>
          <Stack gap="12px" mb="32px">
            {currentMessages.map((message) => {
              const isProcessing = processingIds.has(message.inbox_id);
              return (
                <Box
                  key={message.inbox_id}
                  onClick={() => handleCardClick(message)}
                  position="relative"
                  opacity={isProcessing ? 0.6 : 1}
                  pointerEvents={isProcessing ? 'none' : 'auto'}
                  border="1px solid"
                  borderColor={message.is_read ? 'neutrals.200' : 'warning.500'}
                  borderRadius="12px"
                  p="24px 16px"
                  bg={message.is_read ? 'neutrals.100' : 'warning.100'}
                  cursor="pointer"
                  _hover={{ boxShadow: 'md' }}
                >
                  <Flex gap="24px" align="center">
                    {/* Read/Unread Indicator */}
                    <Box pt="4px">
                      {message.is_read ? (
                        <Box color="success.500">
                          <CheckCircle size={24} weight="fill" />
                        </Box>
                      ) : (
                        <Box color="warning.500">
                          <BellRinging size={24} weight="fill" />
                        </Box>
                      )}
                    </Box>

                    {/* Message Content */}
                    <Box flex="1" minW="0">
                      <Flex justify="space-between" align="start" mb="8px">
                        <Text
                          fontSize="16px"
                          fontWeight="600"
                          color="neutrals.900"
                          maxLines={2}
                          flex="1"
                        >
                          {message.title}
                        </Text>
                        <Text fontSize="12px" color="neutrals.500" whiteSpace="nowrap" ml="16px">
                          {formatDate(message.created_at)}
                        </Text>
                      </Flex>
                      <Box
                        fontSize="14px"
                        color="neutrals.700"
                        css={{
                          '& p': { margin: '0 0 8px 0' },
                          '& p:last-child': { marginBottom: 0 },
                          '& a': {
                            color: 'var(--chakra-colors-blue-600)',
                            textDecoration: 'underline'
                          },
                          '& strong': { fontWeight: 600 },
                          '& em': { fontStyle: 'italic' },
                          '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                          '& li': { marginBottom: '4px' }
                        }}
                        maxH={!expandedMessages.has(message.inbox_id) ? '1.5em' : 'none'}
                        overflow="hidden"
                        lineHeight="1.5"
                        dangerouslySetInnerHTML={{ __html: message.message }}
                      />
                      {message.message.length > 150 && (
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={(e) => toggleExpand(message.inbox_id, e)}
                          mt="4px"
                          p="0"
                          h="auto"
                          minH="auto"
                          color="primary.900"
                          fontWeight="500"
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {expandedMessages.has(message.inbox_id) ? 'Show less' : 'Show more'}
                        </Button>
                      )}
                    </Box>

                    {/* Action Menu */}
                    <Box alignSelf="center">
                      <MenuRoot>
                        <MenuTrigger asChild>
                          <IconButton
                            aria-label="Message actions"
                            size="md"
                            borderRadius="full"
                            _hover={{ bg: 'neutrals.200' }}
                            variant="ghost"
                            onClick={(e) => e.stopPropagation()}
                            disabled={isProcessing}
                          >
                            <DotsThree size={20} weight="bold" />
                          </IconButton>
                        </MenuTrigger>
                        <MenuPositioner>
                          <MenuContent
                            border="1px solid"
                            borderColor="neutrals.200"
                            borderRadius="8px"
                            p="8px"
                          >
                            <MenuItem
                              value="mark-read"
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(message.inbox_id, message.is_read);
                              }}
                            >
                              <Text fontSize="14px">
                                {message.is_read ? 'Mark as unread' : 'Mark as read'}
                              </Text>
                            </MenuItem>
                            <MenuItem
                              value="delete"
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(message.inbox_id);
                              }}
                            >
                              <Text fontSize="14px" color="error.700">
                                Delete
                              </Text>
                            </MenuItem>
                          </MenuContent>
                        </MenuPositioner>
                      </MenuRoot>
                    </Box>
                  </Flex>
                </Box>
              );
            })}
          </Stack>

          {/* Pagination */}
          {totalCount > pageSize && (
            <Flex justify="center" mt="32px">
              <PaginationRoot
                count={totalCount}
                pageSize={pageSize}
                page={currentPage}
                onPageChange={(e) => setCurrentPage(e.page)}
              >
                <Flex align="center" gap="8px">
                  <PaginationPrevTrigger />
                  <PaginationItems />
                  <PaginationNextTrigger />
                </Flex>
              </PaginationRoot>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
}
